<!-- eslint-disable vue/no-v-html -->
<script lang="ts" setup>
import { useSwiper } from '~/composables/common/use-swiper'
import type { Instruction } from '~/types/instruction'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  data: { type: Object as () => Instruction, required: true },
  finalStepLabel: { type: String, default: 'Đã hiểu' },
  onFinished: { type: Function as PropType<() => void>, default: () => {} },
  disableFinal: {
    type: Boolean,
    default: false,
  },
})
const { modules, SwiperSlide, Swiper } = useSwiper()
const emit = defineEmits(['update:modelValue'])
const modalVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const currentStep = ref(1)

const { data } = toRefs(props)

const { isMobile } = useDevice()

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      currentStep.value = 1
    }
  },
)

const swiperRef = ref()

const getRef = (swiperInstance: unknown) => {
  swiperRef.value = swiperInstance
}

const onPreviousClick = () => {
  if (currentStep.value > 1) {
    currentStep.value -= 1
    swiperRef.value?.slideTo(currentStep.value - 1, 300, false)
  }
}

const onNextClick = () => {
  if (currentStep.value === data.value.steps.length) {
    modalVisible.value = false
    props.onFinished()
  } else {
    currentStep.value += 1
    swiperRef.value?.slideTo(currentStep.value - 1, 300, false)
  }
}

const nextLabel = computed(() =>
  data.value.steps.length === 1 || currentStep.value === data.value.steps.length
    ? props.finalStepLabel
    : 'Tiếp tục',
)

const disableFinalButton = computed(() => {
  return props.disableFinal && currentStep.value === data.value.steps.length
})
</script>

<template>
  <BaseInstruction
    v-model="modalVisible"
    :current-step="currentStep"
    :title="isMobile ? data.titleMobile || data.title : data.title"
  >
    <template #description>
      <div class="instruction">
        <Swiper
          :modules="modules"
          :loop="false"
          :navigation="true"
          :auto-height="true"
          @swiper="getRef"
          @slide-change="currentStep = $event.activeIndex + 1"
        >
          <SwiperSlide v-for="(item, index) in data.steps" :key="index">
            <img
              class="instruction__image object-cover"
              :src="isMobile ? item.imageMobile : item.image"
              :alt="`instruction-step-${currentStep}`"
            />
            <div class="instruction__content">
              <div class="instruction__title">{{ item.title }}</div>
              <ul class="instruction__description">
                <li v-for="(text, i) in item.content" :key="i">
                  <span v-html="text"></span>
                </li>
              </ul>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </template>
    <template #footer>
      <div class="base-instruction__footer">
        <div v-show="data.steps.length > 1" class="base-instruction__dots">
          <div
            v-for="(_, index) in data.steps.length"
            :key="index"
            :class="{
              'base-instruction__dot': true,
              'base-instruction__dot--active': index + 1 === currentStep,
            }"
          />
        </div>
        <div class="flex w-full gap-4">
          <BaseButton
            v-show="currentStep > 1"
            variant="outline"
            icon="icon-left"
            class="base-instruction__button-previous"
            button-id="back-button"
            button-name="back-button"
            @click="onPreviousClick"
          />
          <BaseButton
            :label="nextLabel"
            :disabled="disableFinalButton"
            variant="primary"
            class="base-instruction__button-next"
            button-id="next-button"
            button-name="next-button"
            @click="onNextClick"
          />
        </div>
      </div>
    </template>
  </BaseInstruction>
</template>
